import type { WsEvent } from '@jerome2/shared';

// Websocketverbinding met herverbinden (oplopende wachttijd). Gebeurtenissen gaan naar één
// luisteraar; na herverbinden krijgt de app een seintje om toestand opnieuw op te halen. Een
// waakhond let op stilte: de server laat elke 25 s iets van zich horen, dus na een minuut zonder
// bericht is de verbinding dood (slapende laptop, gewisseld netwerk) en verbinden we opnieuw; ook
// zodra het venster terugkomt of het netwerk weer online is, zonder op de wachttijd te wachten.

export interface WsClient {
  close(): void;
  status(): 'verbinden' | 'open' | 'dicht';
}

export interface WsDeps {
  url: string;
  onEvent: (event: WsEvent) => void;
  onReconnect: () => void;
  onStatus?: (status: 'verbinden' | 'open' | 'dicht') => void;
  makeSocket?: (url: string) => WebSocket;
  minDelayMs?: number;
  maxDelayMs?: number;
  /** Na zo lang zonder bericht geldt de verbinding als dood. */
  silenceMs?: number;
}

export function wsUrl(path = '/ws'): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}${path}`;
}

export function connectWs(deps: WsDeps): WsClient {
  const make = deps.makeSocket ?? ((u: string) => new WebSocket(u));
  const minDelay = deps.minDelayMs ?? 1000;
  const maxDelay = deps.maxDelayMs ?? 30_000;
  const silence = deps.silenceMs ?? 60_000;
  let socket: WebSocket | null = null;
  let closed = false;
  let attempts = 0;
  let hadOpen = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let watchdog: ReturnType<typeof setTimeout> | null = null;
  let lastAt = Date.now();
  let status: 'verbinden' | 'open' | 'dicht' = 'verbinden';

  const setStatus = (s: typeof status) => {
    status = s;
    deps.onStatus?.(s);
  };

  const disarm = () => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = null;
  };
  /** Elk bericht (ook een onbekend type) is een teken van leven. */
  const arm = () => {
    lastAt = Date.now();
    disarm();
    watchdog = setTimeout(kick, silence);
  };

  function open() {
    if (closed) return;
    setStatus('verbinden');
    let s: WebSocket;
    try {
      s = make(deps.url);
    } catch {
      schedule();
      return;
    }
    socket = s;
    s.onopen = () => {
      attempts = 0;
      arm();
      setStatus('open');
      if (hadOpen) deps.onReconnect();
      hadOpen = true;
    };
    s.onmessage = (m: MessageEvent) => {
      arm();
      try {
        deps.onEvent(JSON.parse(String(m.data)) as WsEvent);
      } catch {
        // onleesbaar bericht
      }
    };
    s.onclose = (e: CloseEvent) => {
      disarm();
      socket = null;
      setStatus('dicht');
      if (closed) return;
      // Niet ingelogd: ophouden, ook als het venster later terugkomt.
      if (e.code === 4401) teardown();
      else schedule();
    };
    s.onerror = () => {
      // onclose volgt altijd
    };
  }

  function schedule() {
    if (closed || timer) return;
    const delay = Math.min(maxDelay, minDelay * 2 ** Math.min(attempts, 6)) * (0.8 + Math.random() * 0.4);
    attempts += 1;
    timer = setTimeout(() => {
      timer = null;
      open();
    }, delay);
  }

  /** De verbinding is stil of het venster komt terug: de oude socket loslaten (zonder op zijn sluiten te wachten) en meteen opnieuw verbinden. */
  function kick() {
    if (closed) return;
    disarm();
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    const s = socket;
    socket = null;
    if (s) {
      s.onopen = s.onmessage = s.onclose = s.onerror = null;
      try {
        s.close();
      } catch {
        // al dicht
      }
    }
    setStatus('dicht');
    open();
  }

  /** Venster terug of netwerk weer online: een wachtende poging niet afwachten; een open verbinding die te lang stil is, is verdacht. */
  const wake = () => {
    if (closed) return;
    if (status !== 'open' || Date.now() - lastAt > silence) kick();
  };
  const onVisible = () => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') wake();
  };
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
  if (typeof window !== 'undefined') window.addEventListener('online', wake);

  /** Definitief stoppen: geen timers, geen luisteraars meer. */
  function teardown() {
    closed = true;
    disarm();
    if (timer) clearTimeout(timer);
    timer = null;
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    if (typeof window !== 'undefined') window.removeEventListener('online', wake);
  }

  open();
  return {
    close() {
      teardown();
      socket?.close();
    },
    status: () => status,
  };
}
